import type { OceanHabitat } from '../game/engine';
import { HABITAT_INFO, type HabitatStatus } from '../game/habitats';
import { RARITY, SPECIES, speciesById, speciesImage, type HabitatType } from '../lib/aquarium';

// ─────────────────────────────────────────────────────────────────────────────
// A habitat, from its Fish beacon: who lives there, what it counts as, what it
// still needs, and which fish each kind of habitat draws (game/habitats.ts).
// ─────────────────────────────────────────────────────────────────────────────

/** "Rare", in the rarity's colour. */
export function RarityTag({ id }: { id: string }) {
  const r = RARITY[speciesById(id).rarity];
  return <span className="rarity-tag" style={{ color: r.color, borderColor: r.color }}>{r.name}</span>;
}

type Props = { status: HabitatStatus; habitat: OceanHabitat | null; onClose: () => void; onRelease?: (id: string, species: string) => void; onGuide?: () => void };

const TYPES = Object.keys(HABITAT_INFO) as HabitatType[];

export default function HabitatPanel({ status, habitat, onClose, onRelease, onGuide }: Props) {
  const residents = habitat?.residents ?? [];
  const info = status.type ? HABITAT_INFO[status.type] : null;
  return (
    <div className="ocean-modal" role="dialog" aria-modal="true" aria-label="Habitat" onClick={onClose}>
      <div className="ocean-card tray wide" onClick={e => e.stopPropagation()}>
        <div className="row">
          <h3>{info ? `${info.icon} ${info.name}` : '🐠 Fish beacon'}</h3>
          {habitat?.owner && <span className="small muted">{habitat.mine ? 'Yours' : `${habitat.owner}’s`}</span>}
          <span className="spacer" />
          {onGuide && <button type="button" className="small" onClick={onGuide}>📘 How beacons work</button>}
          <button type="button" className="icon" aria-label="Close" onClick={onClose}>✕</button>
        </div>

        {!status.enclosed ? (
          <div className="notice small">🧱 Fish only move into a closed-in space. Wall the beacon in with blocks (no gaps!), with some open water inside for the fish.</div>
        ) : !status.type ? (
          <div className="notice small">🔍 It’s closed in, but not a habitat yet. Build it mostly from one kind of block (see below), then fish will start moving in.</div>
        ) : (
          <p className="small" style={{ margin: 0 }}>
            {residents.length >= status.capacity ? 'It’s full! Add more blocks to make room for more fish.' : 'New fish move in every few minutes while someone is nearby.'}
            {' '}<strong>{residents.length}/{status.capacity} fish</strong> · {status.total} blocks
          </p>
        )}

        <h4 className="recipe-head">Living here</h4>
        {residents.length === 0 ? <p className="small muted">Nobody yet.</p> : (
          <div className="habitat-fish">
            {residents.map(f => (
              <span key={f.id} className="inv-chip"><img src={speciesImage(f.species)} alt="" /> {speciesById(f.species).name} <RarityTag id={f.species} />
                {habitat?.mine && onRelease && (
                  <button type="button" className="icon" title="Let it go back into the ocean" aria-label={`Let the ${speciesById(f.species).name} go`} onClick={() => onRelease(f.id, f.species)}>🌊</button>
                )}
              </span>
            ))}
          </div>
        )}

        <h4 className="recipe-head">What you can build</h4>
        <div className="habitat-guide">
          {TYPES.map(t => {
            const p = status.progress.find(x => x.type === t);
            const fish = SPECIES.filter(s => s.attracts.includes(t));
            return (
              <div key={t} className={`habitat-type${status.type === t ? ' on' : ''}`}>
                <div className="row">
                  <strong>{HABITAT_INFO[t].icon} {HABITAT_INFO[t].name}</strong>
                  <span className="small muted">{HABITAT_INFO[t].blurb}</span>
                  <span className="spacer" />
                  {p && <span className="small">{Math.min(p.have, p.need)}/{p.need}</span>}
                </div>
                {p && <div className="habitat-bar"><span style={{ width: `${Math.min(100, (p.have / p.need) * 100)}%` }} /></div>}
                <div className="habitat-attracts">
                  {fish.map(s => <img key={s.id} src={speciesImage(s.id)} alt={s.name} title={`${s.name} (${RARITY[s.rarity].name})`} style={{ borderColor: RARITY[s.rarity].color }} />)}
                </div>
              </div>
            );
          })}
        </div>
        <p className="small muted" style={{ margin: 0 }}>Only blocks you place count, within 8 blocks of the beacon. The rarer the fish, the less often it comes.</p>
      </div>
    </div>
  );
}
